import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Logo } from "@/components/Logo";

/* Individual staff logins (2026-11) — the landing page for the one-time invite link an admin
 * sends from Administration → Users (create-staff / Resend invite). Mirrors ResetPassword.jsx's
 * layout/styling exactly; unlike that flow, this ONE auto-signs the person in on success
 * (completeStaffInvite, POST /auth/staff/set-password) since there's no existing session to
 * fall back to — it's their very first login. */
export default function StaffSetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { completeStaffInvite } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (newPassword.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (newPassword !== confirmPassword) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      await completeStaffInvite(token, newPassword);
      navigate("/", { replace: true });
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      const code = typeof detail === "object" ? detail.code : detail;
      const message = typeof detail === "object" ? detail.message : null;
      setErr(code === "invalid_or_expired" ? "This invite link is invalid or has expired — ask an admin to resend it." : (message || "Couldn't set your password — please try again."));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-canvas px-4" data-testid="staff-set-password-page">
      <div className="w-full max-w-[380px]">
        <div className="mb-7 flex flex-col items-start gap-4">
          <Logo theme="light" size="md" testId="staff-set-password-logo" />
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-faint">Welcome to NJM OS</div>
            <h2 className="font-editorial text-2xl text-ink mt-1">Set your password</h2>
          </div>
        </div>

        {!token ? (
          <div data-testid="staff-set-password-no-token" className="text-sm text-red-600 bg-red-50 rounded-md px-4 py-4">
            This link is missing its invite token — please use the link from your email exactly as sent.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-[11px] font-medium uppercase tracking-wider text-faint mb-1.5">New password</label>
              <input
                data-testid="staff-set-password-new-input"
                type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                minLength={8} autoFocus
                className="w-full rounded-md border border-hairline bg-white px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 transition-all"
              />
            </div>
            <div>
              <label className="block text-[11px] font-medium uppercase tracking-wider text-faint mb-1.5">Confirm password</label>
              <input
                data-testid="staff-set-password-confirm-input"
                type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                minLength={8}
                className="w-full rounded-md border border-hairline bg-white px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 transition-all"
              />
            </div>
            {err && <div data-testid="staff-set-password-error" className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{err}</div>}
            <button
              data-testid="staff-set-password-submit"
              disabled={busy}
              className="w-full rounded-md bg-brand py-2.5 text-sm font-semibold text-ink hover:bg-brand-dim disabled:opacity-50 transition-colors"
            >{busy ? "Setting up…" : "Set password & sign in"}</button>
          </form>
        )}

        <div className="mt-7 pt-6 border-t border-hairline">
          <a href="/login" data-testid="staff-set-password-back-to-login" className="text-[11px] text-faint hover:text-ink transition-colors">← Back to sign in</a>
        </div>
      </div>
    </div>
  );
}
